/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import { array, bool, string, oneOf } from 'prop-types';
import commonOptionsPropType from 'TMPropTypes/commonOptionsProps';
import styled, { useTheme, css } from 'styled-components';
import { H5 } from 'TMComponents/Typography';
import Pill from 'TMComponents/Pill';
import { spacing } from 'TMDesignSystem/spacing';
import borderRadius from 'TMDesignSystem/borders';
import { breakpoints } from 'TMConstants';
import Checkmark from '../Icons/Checkmark';
import Remove from '../Icons/Remove';
import Content from '../Content';

const success = css`
    background-color: ${({ theme }) => theme.colorSystem.secondary['5']};
`;
const fail = css`
    background-color: ${({ theme }) => theme.colorSystem.validations['4']};
`;
const OVERLAY_BACKGROUND = 'rgba(0, 0, 0, 0.1)';

const info = css`
    border: solid 2px;
    border-color: ${({ theme }) => theme.colorSystem.primary['3']};
    background: ${OVERLAY_BACKGROUND};
`;

const ai = css`
    border: solid 3px;
    border-color: ${({ theme }) => theme.colorSystem.secondary[9]};
    background: ${OVERLAY_BACKGROUND};
    padding: ${spacing.medium};
`;

const outlined = css`
    background: ${({ theme }) => theme.colorSystem.primary['3']};
    border: 2px solid ${({ theme }) => theme.colorSystem.UIText['1']};
    padding: ${spacing.medium};
`;

const blog = css`
    background: ${({ theme }) => theme.colorSystem.UIElements[5]};
    border: 2px solid ${({ theme }) => theme.colorSystem.UIElements[9]};
    padding: ${spacing.mediumLarge};
`;

const panelStyles = {
    success,
    fail,
    info,
    ai,
    outlined,
    blog
};

const PillPlacement = styled.div`
    position: absolute;
    top: 0;
`;

const Wrapper = styled.div`
    ${({ fullWidth }) => (fullWidth ? 'width:100%' : 'max-width: 1120px')};
    ${({ leftAlign }) => leftAlign && 'margin-left: 0px !important;'};
    border-radius: ${borderRadius.medium};
    padding: 0 ${spacing.medium};
    ${({ variant }) => panelStyles[variant]};
`;

const StyledContent = styled(Content)`
    padding: 0;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        & > div > * {
            margin-left: 0 !important;
            margin-right: auto !important;
        }
    }
`;

const IconWrapper = styled.div`
    display: 'inline-block';
    width: ${spacing.mediumLarge};
    height: ${spacing.mediumLarge};
    padding-right: ${spacing.small};
    vertical-align: middle;
`;

const Title = styled(H5)`
    display: inline-block;
    vertical-align: middle;
    margin: auto 0;
`;

const Panel = ({
    displayIcon,
    fullWidth,
    leftAlign,
    variant = 'info',
    title = '',
    content,
    listTickVariant = 'green',
    noUnderline,
    whiteBackground,
    commonOptions
}) => {
    const theme = useTheme(); // eslint-disable-line no-unused-vars
    return (
        <Wrapper
            variant={variant}
            fullWidth={fullWidth}
            leftAlign={leftAlign}>
            {variant === 'ai' && (
                <PillPlacement>
                    <Pill variant="ai" size="small" />
                </PillPlacement>
            )}
            <div>
                {
                    /* istanbul ignore else */
                    displayIcon && (
                        <IconWrapper displayIcon={displayIcon}>
                            {variant === 'success' && (
                                <Checkmark variant="green" />
                            )}
                            {variant === 'fail' && (
                                <Remove
                                    fill={
                                        theme.colorSystem.validations['2']
                                    }
                                />
                            )}
                        </IconWrapper>
                    )
                }
                {title && (
                    <Title variant={commonOptions?.titleVariant}>
                        {title}
                    </Title>
                )}
            </div>
            <StyledContent
                backgroundVariant={variant}
                noSidePadding
                content={content}
                listTickVariant={listTickVariant}
                noUnderline={noUnderline}
                whiteBackground={whiteBackground}
            />
        </Wrapper>
    );
};

Panel.propTypes = {
    variant: oneOf(['info', 'fail', 'success', 'outlined', 'ai', 'blog']),
    fullWidth: bool,
    leftAlign: bool,
    displayIcon: bool,
    title: string,
    listTickVariant: oneOf(['blue', 'green']),
    noUnderline: bool,
    whiteBackground: bool,
    // eslint-disable-next-line react/forbid-prop-types
    content: array,
    ...commonOptionsPropType
};

Panel.defaultProps = {
    variant: 'info',
    fullWidth: false,
    leftAlign: false,
    displayIcon: false,
    title: '',
    listTickVariant: 'green',
    noUnderline: false,
    whiteBackground: false,
    content: []
};

export default Panel;
